import React, { useState, useEffect } from 'react';
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  useMediaQuery,
  FormHelperText,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import HeaderMenu from '../Header/HeaderMenu';
import axiosApi from '../Utils/axiosApi';
import checkPermission from '../Utils/checkPermission';
import './CreateUser.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const CreateUser = ({ user, onClose, isDialog = false }) => {
  const [activeTab, setActiveTab] = useState(0);
  const isMobile = useMediaQuery('(max-width:768px)');

  const [form, setForm] = useState({
    userName: user?.userName || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    systemPassword: user?.systemPassword || '',
    systemConfirmPassword: user?.systemConfirmPassword || '',
    lockIndicator: user?.lockIndicator || 'N', // Y/N
    email: user?.email || '',
    mobileNumber: user?.mobileNumber || '',
    secondaryMobileNumber: user?.secondaryMobileNumber || '',
    address: user?.address || '',
    companyName: user?.companyName || '',
    companyId: user?.companyId || '',
    countryId: user?.countryId || '',
    stateId: user?.stateId || '',
    otpExpiryDateTime: user?.otpExpiryDateTime || '',
    userType: user?.userType || '',
    remarks: user?.remarks || '',
    roleId: user?.roleId || '',
    roleCode: user?.roleCode || '',
    activeUser: user?.activeUser || 'Y', // Y/N
    credentialsNonExpired: user?.credentialsNonExpired ?? true,
    lastLoginDateTime: user?.lastLoginDateTime || '',
    passwordModifyDateTime: user?.passwordModifyDateTime || '',
    ...(user?.id && { id: user.id }), // Include ID for updates
  });

  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'countryId' ? { stateId: '' } : {})
    }));
  };

  const validateForm = () => {
    if (!isDialog && form.systemPassword !== form.systemConfirmPassword) {
      setErrorMessage("Passwords do not match");
      return false;
    }
    if (!form.userName) {
      setErrorMessage("User Name is required");
      return false;
    }
    if (!form.firstName) {
      setErrorMessage("First Name is required");
      return false;
    }
    if (!form.mobileNumber) {
      setErrorMessage("Mobile Number is required");
      return false;
    }
    if (!form.email) {
      setErrorMessage("Email is required");
      return false;
    }
   

    return true;
  };

  useEffect(() => {
    axiosApi.post(`${API_BASE_URL}/country/getCountry`, {})
      .then(response => {
        const countryArray = Array.isArray(response.data)
          ? response.data
          : response.data.countries || [];
        setCountries(countryArray);
      })
      .catch(error => console.error("Error fetching countries:", error));
  }, []);

  useEffect(() => {
    if (form.countryId) {
      axiosApi.post(`${API_BASE_URL}/state/getState`, { countryIds: [form.countryId] })
        .then(response => {
          const stateArray = Array.isArray(response.data)
            ? response.data
            : response.data.states || [];
          setStates(stateArray);
        })
        .catch(error => {
          console.error("Error fetching states:", error);
          setStates([]);
        });
    } else {
      setStates([]);
    }
  }, [form.countryId]);

  useEffect(() => {
    axiosApi.post(`${API_BASE_URL}/company/getCompany`, {})
      .then(response => {
        const companyArray = Array.isArray(response.data)
          ? response.data
          : response.data.companies || [];
        setCompanies(companyArray);
      })
      .catch(error => console.error("Error fetching companies:", error));
  }, []);

  useEffect(() => {
    axiosApi.post(`${API_BASE_URL}/role/getUserRole`, {})
      .then(response => {
        const rolesArray = Array.isArray(response.data)
          ? response.data
          : response.data.userRoles || [];
        setUserRoles(rolesArray);
      })
      .catch(error => console.error("Error fetching user roles:", error));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    const selectedRole = userRoles.find((r) => r.id === form.roleId);
    const selectedCompany = companies.find((c) => c.id === form.companyId);
    const payload = {
      ...form,
      roleCode: selectedRole?.roleCode || '',
      companyName: selectedCompany?.companyName || ''
    };
    const apiUrl = form.id ? '/userManagement/updateUser' : '/userManagement/createUser';

    axiosApi.post(apiUrl, payload)
      .then(response => {
        setSuccessMessage(form.id ? "User updated successfully!" : "User created successfully!");
        setErrorMessage('');
      })
      .catch(error => {
        console.error("Error processing user:", error);
        setErrorMessage("Error processing user. Please try again.");
        setSuccessMessage('');
      });
  };

  const renderInput = (label, name, type = 'text', fullWidth = false) => (
    <Grid item xs={12} md={fullWidth ? 12 : 6}>
      <Typography variant="subtitle2" gutterBottom>{label}</Typography>
      <TextField
        fullWidth
        name={name}
        type={type}
        value={form[name]}
        onChange={handleChange}
        variant="outlined"
        size="small"
      />
      {errorMessage && name === 'systemPassword' && (
        <FormHelperText error>{errorMessage}</FormHelperText>
      )}
    </Grid>
  );

  return (
    <Box className="p-4 max-w-6xl mx-auto">
      {!isDialog && <HeaderMenu />}

      {/* SUCCESS OR ERROR BANNER */}
     {(successMessage || errorMessage) && (
  <div
    style={{
      position: "sticky",
      top: 0,
      zIndex: 999,
      backgroundColor: successMessage ? '#d4edda' : '#f8d7da',
      color: successMessage ? '#155724' : '#721c24',
      border: `1px solid ${successMessage ? '#c3e6cb' : '#f5c6cb'}`,
      padding: '10px 20px',
      borderRadius: '8px',
      marginBottom: '20px',
      textAlign: 'center',
      fontWeight: 'bold',
    }}
  >
    {successMessage || errorMessage}
  </div>
)}


      <Typography variant="h5" fontWeight="bold" gutterBottom>
        {form.id ? 'Edit User' : 'Create User'}
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        variant={isMobile ? 'scrollable' : 'fullWidth'}
        scrollButtons="auto"
        centered={!isMobile}
        sx={{ mb: 3 }}
      >
        <Tab label="Basic Info" />
        <Tab label="Additional Info" />
      </Tabs>

      <Box component="form" onSubmit={handleSubmit}>
        {activeTab === 0 && (
          <Grid container spacing={2}>
            {/* Account Info */}
            {renderInput('Username', 'userName')}
            {renderInput('First Name', 'firstName')}
            {renderInput('Last Name', 'lastName')}
            {!form.id && (
  <>
    {renderInput('Password', 'systemPassword', 'password')}
    {renderInput('Confirm Password', 'systemConfirmPassword', 'password')}
  </>
)}


            {/* Checkboxes */}
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.lockIndicator === 'Y'}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        lockIndicator: e.target.checked ? 'Y' : 'N'
                      }))
                    }
                    name="lockIndicator"
                    color="primary"
                  />
                }
                label="Lock Indicator"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.activeUser === 'Y'}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        activeUser: e.target.checked ? 'Y' : 'N'
                      }))
                    }
                    name="activeUser"
                    color="primary"
                  />
                }
                label="Active User"
              />
            </Grid>

            {/* Contact Info */}
            {renderInput('Email', 'email', 'email')}
            {renderInput('Mobile Number', 'mobileNumber')}
            {renderInput('Secondary Mobile Number', 'secondaryMobileNumber')}
            {renderInput('Address', 'address', 'text', true)}
          </Grid>
        )}

        {activeTab === 1 && (
          <Grid container spacing={2}>
            {/* Company Info */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Company Name</Typography>
              <FormControl fullWidth size="small" required>
                <Select
                  name="companyId"
                  value={form.companyId}
                  onChange={handleChange}
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.companyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Country</Typography>
              <FormControl fullWidth size="small" required>
                <Select
                  name="countryId"
                  value={form.countryId}
                  onChange={handleChange}
                >
                  {countries.map((country) => (
                    <MenuItem key={country.id} value={country.id}>
                      {country.countryName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>State</Typography>
              <FormControl fullWidth size="small">
                <Select
                  name="stateId"
                  value={form.stateId}
                  onChange={handleChange}
                  disabled={!form.countryId}
                >
                  {states.map((state) => (
                    <MenuItem key={state.id} value={state.id}>
                      {state.stateName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* System Fields */}
            
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>User Role</Typography>
              <FormControl fullWidth size="small" required>
                <Select
                  name="roleId"
                  value={form.roleId}
                  onChange={handleChange}
                >
                  {userRoles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.description}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {renderInput('Remarks', 'remarks', 'text', true)}
          </Grid>
        )}

        <Box textAlign="center" mt={4}>
          <Button disabled={!checkPermission("Admin", "Create User", "CREATE")} type="submit" variant="contained" color="primary">
            {form.id ? 'Update User' : 'Create User'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateUser;
